import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, Alert, KeyboardAvoidingView, Platform } from 'react-native';
import { TextInput, Button, Text, Card, ProgressBar, RadioButton, Divider } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import api from '../../services/api';

const RegistroEmpresaScreen = ({ navigation }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [planes, setPlanes] = useState([]);
  const [solicitudId, setSolicitudId] = useState(null);

  // Datos del formulario
  const [form, setForm] = useState({
    nombre_empresa: '',
    slug: '',
    email_contacto: '',
    telefono: '',
    direccion: '',
    rtn: '',
    nombre_admin: '',
    email_admin: '',
    password_admin: '',
    password_confirm: '',
    plan_id: '',
    notas: '',
  });

  useEffect(() => {
    loadPlanes();
  }, []);

  const loadPlanes = async () => {
    try {
      const data = await api.getPlanesPublicos();
      setPlanes(data);
      if (data.length > 0) {
        setForm(prev => ({ ...prev, plan_id: String(data[0].id) }));
      }
    } catch (error) {
      console.log('Error cargando planes:', error);
    }
  };

  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const updateField = (field, value) => {
    setForm(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'nombre_empresa') {
        updated.slug = generateSlug(value);
      }
      return updated;
    });
  };

  const validateStep1 = () => {
    if (!form.nombre_empresa.trim()) {
      Alert.alert('Error', 'El nombre de la empresa es obligatorio');
      return false;
    }
    if (!form.slug.trim()) {
      Alert.alert('Error', 'El codigo de empresa es obligatorio');
      return false;
    }
    if (!form.email_contacto.trim()) {
      Alert.alert('Error', 'El email de contacto es obligatorio');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!form.nombre_admin.trim()) {
      Alert.alert('Error', 'El nombre del administrador es obligatorio');
      return false;
    }
    if (!form.email_admin.trim()) {
      Alert.alert('Error', 'El email del administrador es obligatorio');
      return false;
    }
    if (!form.password_admin || form.password_admin.length < 6) {
      Alert.alert('Error', 'La contrasena debe tener al menos 6 caracteres');
      return false;
    }
    if (form.password_admin !== form.password_confirm) {
      Alert.alert('Error', 'Las contrasenas no coinciden');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    }
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const result = await api.registrarEmpresa({
        nombre_empresa: form.nombre_empresa.trim(),
        slug: form.slug.trim(),
        email_contacto: form.email_contacto.trim(),
        telefono: form.telefono.trim(),
        direccion: form.direccion.trim(),
        rtn: form.rtn.trim(),
        nombre_admin: form.nombre_admin.trim(),
        email_admin: form.email_admin.trim(),
        password_admin: form.password_admin,
        plan_id: parseInt(form.plan_id) || 1,
        notas: form.notas.trim(),
      });

      setSolicitudId(result.solicitud_id || result.id);
      setStep(4); // Pantalla de exito
    } catch (error) {
      Alert.alert('Error', error.message || 'No se pudo enviar la solicitud');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <View>
      <Text style={styles.stepTitle}>Datos de la Empresa</Text>
      <Text style={styles.stepDescription}>Ingresa la informacion de tu empresa o distribuidora</Text>

      <TextInput
        label="Nombre de la empresa *"
        value={form.nombre_empresa}
        onChangeText={(v) => updateField('nombre_empresa', v)}
        style={styles.input}
        left={<TextInput.Icon icon="domain" />}
      />

      <TextInput
        label="Codigo de empresa (slug) *"
        value={form.slug}
        onChangeText={(v) => updateField('slug', v)}
        style={styles.input}
        autoCapitalize="none"
        left={<TextInput.Icon icon="link" />}
        placeholder="ej: mi-distribuidora"
      />
      <Text style={styles.hint}>Este codigo se usara para iniciar sesion</Text>

      <TextInput
        label="Email de contacto *"
        value={form.email_contacto}
        onChangeText={(v) => updateField('email_contacto', v)}
        style={styles.input}
        keyboardType="email-address"
        autoCapitalize="none"
        left={<TextInput.Icon icon="email" />}
      />

      <TextInput
        label="Telefono"
        value={form.telefono}
        onChangeText={(v) => updateField('telefono', v)}
        style={styles.input}
        keyboardType="phone-pad"
        left={<TextInput.Icon icon="phone" />}
      />

      <TextInput
        label="Direccion"
        value={form.direccion}
        onChangeText={(v) => updateField('direccion', v)}
        style={styles.input}
        multiline
        left={<TextInput.Icon icon="map-marker" />}
      />

      <TextInput
        label="RTN"
        value={form.rtn}
        onChangeText={(v) => updateField('rtn', v)}
        style={styles.input}
        left={<TextInput.Icon icon="card-account-details" />}
      />
    </View>
  );

  const renderStep2 = () => (
    <View>
      <Text style={styles.stepTitle}>Cuenta de Administrador</Text>
      <Text style={styles.stepDescription}>Crea la cuenta del administrador principal de tu empresa</Text>

      <TextInput
        label="Nombre completo *"
        value={form.nombre_admin}
        onChangeText={(v) => updateField('nombre_admin', v)}
        style={styles.input}
        left={<TextInput.Icon icon="account" />}
      />

      <TextInput
        label="Email del administrador *"
        value={form.email_admin}
        onChangeText={(v) => updateField('email_admin', v)}
        style={styles.input}
        keyboardType="email-address"
        autoCapitalize="none"
        left={<TextInput.Icon icon="email" />}
      />

      <TextInput
        label="Contrasena *"
        value={form.password_admin}
        onChangeText={(v) => updateField('password_admin', v)}
        style={styles.input}
        secureTextEntry
        left={<TextInput.Icon icon="lock" />}
      />

      <TextInput
        label="Confirmar contrasena *"
        value={form.password_confirm}
        onChangeText={(v) => updateField('password_confirm', v)}
        style={styles.input}
        secureTextEntry
        left={<TextInput.Icon icon="lock-check" />}
      />
    </View>
  );

  const renderStep3 = () => (
    <View>
      <Text style={styles.stepTitle}>Plan y Confirmacion</Text>
      <Text style={styles.stepDescription}>Selecciona un plan para tu empresa</Text>

      <RadioButton.Group onValueChange={(v) => updateField('plan_id', v)} value={form.plan_id}>
        {planes.map((plan) => (
          <Card key={plan.id} style={[styles.planCard, form.plan_id === String(plan.id) && styles.planCardSelected]}>
            <Card.Content style={styles.planContent}>
              <View style={styles.planHeader}>
                <RadioButton value={String(plan.id)} />
                <View style={{ flex: 1 }}>
                  <Text style={styles.planName}>{plan.nombre}</Text>
                  <Text style={styles.planPrice}>
                    {plan.precio_mensual > 0 ? `L. ${plan.precio_mensual}/mes` : 'Gratuito'}
                  </Text>
                </View>
              </View>
              {plan.descripcion && (
                <Text style={styles.planDesc}>{plan.descripcion}</Text>
              )}
              <Text style={styles.planLimits}>
                {plan.max_usuarios > 0 ? `${plan.max_usuarios} usuarios` : 'Usuarios ilimitados'}
                {' | '}
                {plan.max_productos > 0 ? `${plan.max_productos} productos` : 'Productos ilimitados'}
              </Text>
            </Card.Content>
          </Card>
        ))}
      </RadioButton.Group>

      <TextInput
        label="Notas adicionales (opcional)"
        value={form.notas}
        onChangeText={(v) => updateField('notas', v)}
        style={styles.input}
        multiline
        numberOfLines={3}
        left={<TextInput.Icon icon="note-text" />}
      />

      <Divider style={{ marginVertical: 16 }} />

      <Card style={styles.summaryCard}>
        <Card.Content>
          <Text style={styles.summaryTitle}>Resumen</Text>
          <Text style={styles.summaryItem}>Empresa: {form.nombre_empresa}</Text>
          <Text style={styles.summaryItem}>Codigo: {form.slug}</Text>
          <Text style={styles.summaryItem}>Email: {form.email_contacto}</Text>
          <Text style={styles.summaryItem}>Admin: {form.nombre_admin} ({form.email_admin})</Text>
        </Card.Content>
      </Card>
    </View>
  );

  const renderSuccess = () => (
    <View style={styles.successContainer}>
      <Ionicons name="checkmark-circle" size={80} color="#4CAF50" />
      <Text style={styles.successTitle}>Solicitud Enviada</Text>
      <Text style={styles.successText}>
        Tu solicitud de registro ha sido enviada exitosamente. Un administrador revisara tu solicitud y te notificara cuando sea aprobada.
      </Text>
      {solicitudId && (
        <Text style={styles.solicitudId}>ID de solicitud: #{solicitudId}</Text>
      )}
      <Button
        mode="contained"
        onPress={() => navigation.navigate('EstadoSolicitud', { solicitudId })}
        style={[styles.button, { marginTop: 20 }]}
      >
        Consultar Estado
      </Button>
      <Button
        mode="text"
        onPress={() => navigation.goBack()}
        style={{ marginTop: 10 }}
      >
        Volver al Login
      </Button>
    </View>
  );

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={styles.container}
    >
      <ScrollView contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled">
        {/* Header */}
        <View style={styles.header}>
          <Button icon="arrow-left" onPress={() => navigation.goBack()} textColor="#007bff">
            Volver
          </Button>
          <Text style={styles.headerTitle}>Registrar Empresa</Text>
        </View>

        {step < 4 && (
          <>
            {/* Progress */}
            <View style={styles.progressContainer}>
              <ProgressBar progress={step / 3} color="#007bff" style={styles.progressBar} />
              <Text style={styles.progressText}>Paso {step} de 3</Text>
            </View>

            {/* Steps */}
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}

            {/* Navigation buttons */}
            <View style={styles.buttonRow}>
              {step > 1 && (
                <Button mode="outlined" onPress={handleBack} style={[styles.button, { flex: 1, marginRight: 8 }]}>
                  Anterior
                </Button>
              )}
              {step < 3 ? (
                <Button mode="contained" onPress={handleNext} style={[styles.button, { flex: 1, backgroundColor: '#007bff' }]}>
                  Siguiente
                </Button>
              ) : (
                <Button
                  mode="contained"
                  onPress={handleSubmit}
                  loading={loading}
                  disabled={loading}
                  style={[styles.button, { flex: 1, backgroundColor: '#4CAF50' }]}
                >
                  Enviar Solicitud
                </Button>
              )}
            </View>
          </>
        )}

        {step === 4 && renderSuccess()}
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  scrollContent: { padding: 16, paddingBottom: 40 },
  header: { flexDirection: 'row', alignItems: 'center', marginBottom: 16 },
  headerTitle: { fontSize: 20, fontWeight: 'bold', color: '#333', marginLeft: 8 },
  progressContainer: { marginBottom: 20 },
  progressBar: { height: 6, borderRadius: 3 },
  progressText: { textAlign: 'center', color: '#666', marginTop: 4, fontSize: 13 },
  stepTitle: { fontSize: 20, fontWeight: 'bold', color: '#333', marginBottom: 4 },
  stepDescription: { fontSize: 14, color: '#666', marginBottom: 16 },
  input: { marginBottom: 12, backgroundColor: 'white' },
  hint: { fontSize: 12, color: '#888', marginTop: -8, marginBottom: 12, marginLeft: 4 },
  buttonRow: { flexDirection: 'row', marginTop: 20 },
  button: { paddingVertical: 4 },
  planCard: { marginBottom: 10, backgroundColor: 'white' },
  planCardSelected: { borderColor: '#007bff', borderWidth: 2 },
  planContent: {},
  planHeader: { flexDirection: 'row', alignItems: 'center' },
  planName: { fontSize: 16, fontWeight: 'bold', color: '#333' },
  planPrice: { fontSize: 14, color: '#007bff', fontWeight: '600' },
  planDesc: { fontSize: 13, color: '#666', marginTop: 4, marginLeft: 40 },
  planLimits: { fontSize: 12, color: '#888', marginTop: 4, marginLeft: 40 },
  summaryCard: { backgroundColor: '#f0f8ff' },
  summaryTitle: { fontSize: 16, fontWeight: 'bold', color: '#333', marginBottom: 8 },
  summaryItem: { fontSize: 14, color: '#555', marginBottom: 4 },
  successContainer: { alignItems: 'center', paddingVertical: 40 },
  successTitle: { fontSize: 24, fontWeight: 'bold', color: '#4CAF50', marginTop: 16 },
  successText: { fontSize: 15, color: '#555', textAlign: 'center', marginTop: 12, lineHeight: 22 },
  solicitudId: { fontSize: 16, color: '#007bff', fontWeight: '600', marginTop: 16 },
});

export default RegistroEmpresaScreen;
